/**
 * Enhanced Tooltip Component
 *
 * Wraps Chakra UI's Tooltip with:
 * - Auto-dismisses after 3 seconds on ALL platforms (safety net for stuck tooltips)
 * - Tap-to-open on mobile/touch devices
 * - Click-away dismissal on mobile
 *
 * Use this instead of importing Tooltip directly from @chakra-ui/react.
 *
 * @example
 * ```tsx
 * import { Tooltip } from '../ui/Tooltip';
 *
 * <Tooltip label="Click me">
 *   <Button>Hover me</Button>
 * </Tooltip>
 * ```
 */

import { Tooltip as ChakraTooltip } from '@chakra-ui/react';
import type { TooltipProps as ChakraTooltipProps } from '@chakra-ui/react';
import { cloneElement, isValidElement } from 'react';
import type { ReactElement } from 'react';
import { useMobileTooltip } from '../../hooks/ui/useMobileTooltip';

export interface TooltipProps extends ChakraTooltipProps {
  /**
   * Auto-dismiss delay in milliseconds
   * Default: 3000ms (3 seconds)
   */
  autoDismissDelay?: number;
  /**
   * Whether to enable enhanced behavior (auto-dismiss, mobile tap)
   * Default: true
   */
  enableEnhancedBehavior?: boolean;
  /** @deprecated Use enableEnhancedBehavior instead */
  enableMobileBehavior?: boolean;
  /** @deprecated Use autoDismissDelay instead */
  mobileAutoDismissDelay?: number;
}

/**
 * Enhanced Tooltip with auto-dismiss on all platforms
 */
export function Tooltip({
  autoDismissDelay,
  mobileAutoDismissDelay,
  enableEnhancedBehavior,
  enableMobileBehavior,
  children,
  ...tooltipProps
}: Readonly<TooltipProps>) {
  const delay = autoDismissDelay ?? mobileAutoDismissDelay ?? 3000;
  const enabled = enableEnhancedBehavior ?? enableMobileBehavior ?? true;

  const enhancedProps = useMobileTooltip({
    autoDismissDelay: delay,
    enabled,
  });

  // On mobile, add tap handlers to open the tooltip
  const enhancedChildren =
    enhancedProps?.isMobileDevice && isValidElement(children)
      ? cloneElement(children as ReactElement<any>, {
          onTouchStart: (e: React.TouchEvent) => {
            enhancedProps.onOpen();
            if ((children as ReactElement<any>).props?.onTouchStart) {
              (children as ReactElement<any>).props.onTouchStart(e);
            }
          },
          onClick: (e: React.MouseEvent) => {
            enhancedProps.onOpen();
            if ((children as ReactElement<any>).props?.onClick) {
              (children as ReactElement<any>).props.onClick(e);
            }
          },
          'data-tooltip-trigger': true,
        })
      : children;

  const finalProps = enhancedProps
    ? {
        ...tooltipProps,
        isOpen: enhancedProps.isOpen,
        onOpen: enhancedProps.onOpen,
        onClose: enhancedProps.onClose,
        openDelay: enhancedProps.openDelay,
        closeDelay: enhancedProps.closeDelay,
      }
    : tooltipProps;

  return <ChakraTooltip {...finalProps}>{enhancedChildren}</ChakraTooltip>;
}
